import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import UserModal from './UserModal';
import ContentstackService from '../services/contentstack';
// localStorage removed - using pure Contentstack approach
import './LandingPage.css';

const LandingPage = ({ userInfo, setUserInfo }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [landingContent, setLandingContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newsletterStatus, setNewsletterStatus] = useState(null);
  const navigate = useNavigate();

  // Fetch landing page content from CMS
  useEffect(() => {
    const fetchLandingContent = async () => {
      try {
        setLoading(true);
        const content = await ContentstackService.getLandingPageContent();
        setLandingContent(content);
      } catch (error) {
        console.error('Failed to fetch landing page content:', error);
        // Set default content if CMS fails
        setLandingContent({
          hero_banner_section: {
            heading: 'Build Your Perfect Resume in Minutes',
            rich_text_editor: 'Create a professional, ATS-friendly resume with our modern builder. Choose from beautiful templates and land your dream job faster.',
            get_started: 'Create My Resume'
          }
        });
      } finally {
        setLoading(false);
      }
    };

    fetchLandingContent();
  }, []);

  const handleGetStarted = () => {
    // Always redirect directly to resume builder (no modal)
    navigate('/resume-builder');
  };

  const handleLogin = () => {
    // Show modal to collect user name and email
    setIsModalOpen(true);
  };

  const handleModalSubmit = async (userData) => {
    try {
      // Save user data to app state only (no localStorage)
      
      // Create subscriber entry in CMS
      await ContentstackService.createSubscriber(userData);
      console.log('Subscriber created successfully');
    } catch (error) {
      console.error('Failed to create subscriber:', error);
      // Continue anyway - don't block user flow
    }
    
    setUserInfo(userData);
    setIsModalOpen(false);
    navigate('/resume-builder');
  };

  // Handle newsletter signup
  const handleNewsletterSignup = async () => {
    const emailInput = document.getElementById('newsletter-email');
    const email = emailInput?.value?.trim();

    if (!email) {
      setNewsletterStatus({ type: 'error', message: 'Please enter your email address.' });
      setTimeout(() => setNewsletterStatus(null), 3000);
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setNewsletterStatus({ type: 'error', message: 'Please enter a valid email address.' });
      setTimeout(() => setNewsletterStatus(null), 3000);
      return;
    }

    try {
      setNewsletterStatus({ type: 'loading', message: 'Subscribing...' });
      
      // Create subscriber with just email (name can be empty for newsletter)
      await ContentstackService.createSubscriber({
        name: 'Newsletter Subscriber',
        email: email
      });

      setNewsletterStatus({ type: 'success', message: 'Successfully subscribed! Thank you for joining our newsletter.' });
      emailInput.value = ''; // Clear the input
      setTimeout(() => setNewsletterStatus(null), 5000);
    } catch (error) {
      console.error('Newsletter signup failed:', error);
      setNewsletterStatus({ type: 'error', message: 'Subscription failed. Please try again later.' });
      setTimeout(() => setNewsletterStatus(null), 3000);
    }
  };

  return (
    <div className="landing-page">
      {/* Navigation */}
      <nav className="navbar">
        <div className="nav-container">
          <div className="nav-logo">
            <h2>ResumeBuilder</h2>
          </div>
          <div className="nav-menu">
            {/* Navigation buttons removed - using only hero section buttons */}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-section">
        {loading ? (
          <div className="hero-container">
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Loading...</p>
            </div>
          </div>
        ) : (
          <div className="hero-container">
            <div className="hero-content fade-in">
              <h1 className="hero-title">
                {landingContent?.hero_banner_section?.heading || 'Build Your Perfect Resume in'}
                <span className="gradient-text"> Minutes</span>
              </h1>
              <p 
                className="hero-description"
                dangerouslySetInnerHTML={{
                  __html: landingContent?.hero_banner_section?.rich_text_editor || 
                         'Create a professional, ATS-friendly resume with our modern builder. Choose from beautiful templates and land your dream job faster.'
                }}
              ></p>
              <div className="hero-buttons">
                <button className="btn btn-primary btn-large" onClick={handleGetStarted}>
                  Get Started
                </button>
                <button className="btn btn-secondary btn-large" onClick={handleLogin} style={{ marginLeft: '16px' }}>
                  Login
                </button>
              </div>
            </div>
            <div className="hero-image fade-in">
              <div className="resume-preview">
                <div className="resume-header">
                  <div className="resume-avatar"></div>
                  <div className="resume-info">
                    <div className="resume-name"></div>
                    <div className="resume-title"></div>
                  </div>
                </div>
                <div className="resume-sections">
                  <div className="resume-section"></div>
                  <div className="resume-section"></div>
                  <div className="resume-section"></div>
                </div>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="features-container">
          <h2 className="features-title">Why Choose Our Resume Builder?</h2>
          <div className="features-grid">
            <div className="feature-card slide-up">
              <div className="feature-icon">🎨</div>
              <h3>Modern Templates</h3>
              <p>Choose from professionally designed templates that stand out to recruiters.</p>
            </div>
            <div className="feature-card slide-up">
              <div className="feature-icon">⚡</div>
              <h3>Quick & Easy</h3>
              <p>Build your resume in minutes with our intuitive drag-and-drop interface.</p>
            </div>
            <div className="feature-card slide-up">
              <div className="feature-icon">📱</div>
              <h3>Mobile Friendly</h3>
              <p>Create and edit your resume on any device, anywhere, anytime.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter/Subscribe Section */}
      <section className="newsletter-section">
        <div className="newsletter-container">
          <div className="newsletter-content">
            <h2 className="newsletter-title">Stay Updated</h2>
            <p className="newsletter-description">
              Get the latest resume tips, job search strategies, and career advice delivered to your inbox.
            </p>
            <div className="newsletter-form">
              <input 
                type="email" 
                placeholder="Enter your email address"
                className="newsletter-input"
                id="newsletter-email"
              />
              <button 
                className="btn btn-primary newsletter-btn"
                onClick={handleNewsletterSignup}
                disabled={newsletterStatus?.type === 'loading'}
              >
                {newsletterStatus?.type === 'loading' ? 'Subscribing...' : 'Subscribe'}
              </button>
            </div>
            {newsletterStatus && (
              <div className={`newsletter-status ${newsletterStatus.type}`}>
                {newsletterStatus.message}
              </div>
            )}
            <p className="newsletter-disclaimer">
              No spam, unsubscribe at any time. We respect your privacy.
            </p>
          </div>
        </div>
      </section>

      {/* User Modal */}
      {isModalOpen && (
        <UserModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSubmit={handleModalSubmit}
        />
      )}
    </div>
  );
};

export default LandingPage;
